import { FC, useEffect, useState } from 'react'
import { debounce } from 'lodash'
import { SearchPayee } from '@entities/tsp'
import { Partners, usePartner } from '@shared/lib/partner'
import { useCategoryServices } from '../model/useCategoryServices'

interface ChildrenProps {
  searchTerm: string
  onSearchTermChange: (value: string) => void
  suggestions: SearchPayee[]
  isReady: boolean
}

interface TspAutoCompleteProps {
  servicesForSearch?: SearchPayee[]
  children: (props: ChildrenProps) => JSX.Element
}

const TspAutoComplete: FC<TspAutoCompleteProps> = ({ servicesForSearch, children }) => {
  const { partner } = usePartner()
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [suggestions, setSuggestions] = useState<SearchPayee[]>([])
  const [isReady, setIsReady] = useState(false)

  const { servicesAll, fetchCategoryData, isCategoryServicesLoading } = useCategoryServices()

  const shouldFetchServices = servicesForSearch === undefined
  const services = servicesForSearch ?? servicesAll

  useEffect(() => {
    if (shouldFetchServices) {
      fetchCategoryData()
    }
  }, [shouldFetchServices, fetchCategoryData])

  useEffect(() => {
    if (!searchTerm) {
      setSuggestions([])
      setIsReady(false)

      return
    }

    if (shouldFetchServices && isCategoryServicesLoading) {
      setIsReady(false)

      return
    }

    setIsReady(false)

    const handler = debounce(() => {
      setSuggestions(
        services.filter((payee) => {
          const matchesSearch = payee.providerName.toLowerCase().includes(searchTerm.toLowerCase())
          const isAllowedForPartner = partner === Partners.DEFAULT || payee.features?.platform_type !== 'business'

          return matchesSearch && isAllowedForPartner
        })
      )
      setIsReady(true)
    }, 300)

    handler()

    return () => handler.cancel()
  }, [searchTerm, services, partner, shouldFetchServices, isCategoryServicesLoading])

  const onSearchTermChange = (value: string) => {
    setSearchTerm(value)
  }

  return children({ searchTerm, onSearchTermChange, suggestions, isReady })
}

export default TspAutoComplete
